<template>
  <div class="create-package-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>{{ isEditMode ? $t('wholesaler.CreatePackage.editTitle') : $t('wholesaler.CreatePackage.createTitle') }}</h1>
      <div class="header-actions">
        <div class="header-icon" @click="savePackage">💾</div>
      </div>
    </header>

    <!-- 表单内容 -->
    <div class="form-container">
      <!-- 基本信息 -->
      <div class="form-section">
        <div class="section-title">{{ $t('wholesaler.CreatePackage.basicInfo') }}</div>
        <div class="form-group">
          <label>{{ $t('wholesaler.CreatePackage.packageName') }} <span class="required">*</span></label>
          <input type="text" v-model="form.packageName" :placeholder="$t('wholesaler.CreatePackage.packageNamePlaceholder')" class="form-input">
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.CreatePackage.description') }}</label>
          <textarea v-model="form.description" :placeholder="$t('wholesaler.CreatePackage.descriptionPlaceholder')" class="form-textarea" rows="3"></textarea>
        </div>
        <div class="form-group">
          <label>{{ $t('wholesaler.CreatePackage.validPeriod') }}</label>
          <div class="date-range">
            <input type="date" v-model="form.validStartDate" class="form-input">
            <span>{{ $t('wholesaler.CreatePackage.to') }}</span>
            <input type="date" v-model="form.validEndDate" class="form-input">
          </div>
        </div>
      </div>

      <!-- 选择商品 -->
      <div class="form-section">
        <div class="section-title">{{ $t('wholesaler.CreatePackage.selectProducts') }} <span class="required">*</span></div>
        
        <!-- 批量调减栏 -->
        <div class="batch-adjust-bar">
          <span class="batch-label">{{ $t('wholesaler.CreatePackage.batchAdjust') }}</span>
          <input 
            type="number" 
            v-model.number="batchAdjustValue" 
            :placeholder="$t('wholesaler.CreatePackage.adjustAmount')" 
            step="0.5"
            class="batch-input"
          />
          <button @click="applyBatchAdjust" class="batch-btn">{{ $t('wholesaler.CreatePackage.apply') }}</button>
        </div>
        
        <!-- 快捷折扣标签 -->
        <div class="discount-tags">
          <span 
            v-for="discount in [9, 8.5, 8, 7.5, 7]" 
            :key="discount"
            class="discount-tag"
            @click="applyDiscount(discount)"
          >
            {{ discount }}{{ $t('wholesaler.CreatePackage.discount') }}
          </span>
        </div>
        
        <div class="product-selector">
          <div class="selector-header">
            <input type="checkbox" v-model="selectAllProducts" @change="handleSelectAllProducts" class="select-all">
            <span>{{ $t('wholesaler.CreatePackage.selectAll') }}</span>
          </div>
          <div class="product-list">
            <div v-for="product in availableProducts" :key="product.id" class="product-item" :class="{ selected: selectedProductIds.includes(product.id) }">
              <input 
                type="checkbox" 
                :value="product.id" 
                v-model="selectedProductIds" 
                @change="handleProductCheck(product.id, selectedProductIds.includes(product.id))"
                class="product-checkbox"
              >
              <div class="product-image">{{ getProductIcon(product) }}</div>
              <div class="product-info">
                <div class="product-name">{{ product.name }}</div>
                <div class="product-meta">
                  {{ $t('wholesaler.CreatePackage.marketPrice') }} ¥{{ product.marketPrice }}
                  <span v-if="selectedProductIds.includes(product.id) && productPrices[product.id]" class="save-tag">
                    {{ $t('wholesaler.CreatePackage.save') }}¥{{ (product.marketPrice - productPrices[product.id]).toFixed(2) }}
                  </span>
                  <span v-if="selectedProductIds.includes(product.id) && productPrices[product.id]" class="discount-badge">
                    {{ getDiscountText(productPrices[product.id], product.marketPrice) }}{{ $t('wholesaler.CreatePackage.discount') }}
                  </span>
                </div>
              </div>
              <div class="product-price-input" v-if="selectedProductIds.includes(product.id)">
                <label>{{ $t('wholesaler.CreatePackage.wholesalePrice') }}</label>
                <input 
                  type="number" 
                  v-model="productPrices[product.id]" 
                  placeholder="0.00" 
                  step="0.0001" 
                  class="price-input"
                  :disabled="!selectedProductIds.includes(product.id)"
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 选择档口 -->
      <div class="form-section">
        <div class="section-title">
          {{ $t('wholesaler.CreatePackage.selectStalls') }} <span class="required">*</span>
          <button v-if="selectedCustomerIds.length > 0" @click="clearAllCustomers" class="btn-clear-all">
            {{ $t('wholesaler.CreatePackage.clearAll') }} ({{ selectedCustomerIds.length }})
          </button>
        </div>
        
        <!-- 第 1 层：已选档口列表（点击可删除） -->
        <div class="selected-layer" v-if="selectedCustomerIds.length > 0">
          <div class="selected-item" v-for="id in selectedCustomerIds" :key="id" @click="removeCustomer(id)">
            🏪 {{ getCustomerName(id) }}
            <span class="remove-hint">×</span>
          </div>
        </div>
        
        <!-- 第 2 层：搜索框 -->
        <div class="search-layer">
          <input 
            type="text" 
            v-model="customerSearchQuery"
            :placeholder="$t('wholesaler.CreatePackage.searchStallPlaceholder')"
            class="search-input-full"
          >
        </div>
        
        <!-- 第 3 层：历史选择（按批发报价分组） -->
        <div class="history-layer" v-if="historyPackages.length > 0 && !customerSearchQuery">
          <div class="history-section-title">
            {{ $t('wholesaler.CreatePackage.historySelection') }}
          </div>
          <div class="history-packages">
            <div 
              v-for="pkg in historyPackages.slice(0, 10)" 
              :key="pkg.id"
              :class="['history-package-card', { 'is-selected': hasPackageCustomers(pkg) }]"
              @click="addPackageCustomers(pkg)"
            >
              <div class="package-card-header">
                <span class="package-card-name">📦 {{ pkg.packageName }}</span>
                <div class="package-card-meta">
                  <span class="package-card-date">{{ pkg.createdDate }}</span>
                  <span class="package-card-count">{{ pkg.customers.length }} {{ $t('wholesaler.CreatePackage.stalls') }}</span>
                </div>
              </div>
              <div v-if="hasPackageCustomers(pkg)" class="package-selected-mark">
                ✓ {{ $t('wholesaler.CreatePackage.added') }} {{ getSelectedPackageCount(pkg) }}/{{ pkg.customers.length }} {{ $t('wholesaler.CreatePackage.stalls') }}
              </div>
            </div>
          </div>
          <div v-if="historyPackages.length > 10" class="history-more">
            {{ $t('wholesaler.CreatePackage.showRecentPackages') }} {{ historyPackages.length }} {{ $t('wholesaler.CreatePackage.totalPackages') }}
          </div>
        </div>
        
        <!-- 第 4 层：全部档口（默认显示分页第一页） -->
        <div class="results-layer">
          <div class="results-header">
            <span class="results-title">{{ customerSearchQuery ? $t('wholesaler.CreatePackage.searchResults') : $t('wholesaler.CreatePackage.allStalls') }}</span>
            <span class="results-count">{{ $t('wholesaler.CreatePackage.selected') }} {{ selectedCustomerIds.length }} / {{ filteredCustomers.length }} {{ $t('wholesaler.CreatePackage.count') }}</span>
            <div class="select-all-group">
              <button @click="selectAllOnPage" class="btn-select-all">{{ $t('wholesaler.CreatePackage.selectAllOnPage') }}</button>
              <button @click="doSelectAllCustomers" class="btn-select-all btn-select-all-primary">{{ $t('wholesaler.CreatePackage.allStalls') }}</button>
              <button v-if="customerSearchQuery" @click="clearSearch" class="btn-clear-search">{{ $t('wholesaler.CreatePackage.clear') }}</button>
            </div>
          </div>
          <div class="results-list">
            <div 
              v-for="customer in paginatedCustomers" 
              :key="customer.id"
              :class="['result-item', { 'is-selected': selectedCustomerIds.includes(customer.id) }]"
              @click="addCustomer(customer.id)"
            >
              <div class="result-content">
                <span class="result-name">🏪 {{ customer.name }}</span>
                <span class="result-meta">{{ customer.contactPerson || '' }} {{ customer.contactPhone || '' }}</span>
              </div>
              <div v-if="selectedCustomerIds.includes(customer.id)" class="selected-mark">✓ {{ $t('wholesaler.CreatePackage.added') }}</div>
            </div>
          </div>
          <!-- 分页 -->
          <div class="pagination" v-if="customerTotalPages > 1">
            <button :disabled="customerPage === 1" @click="customerPage--" class="btn-page">{{ $t('wholesaler.CreatePackage.prevPage') }}</button>
            <span class="page-info">{{ $t('wholesaler.CreatePackage.pageInfo', { page: customerPage, totalPages: customerTotalPages }) }}</span>
            <button :disabled="customerPage >= customerTotalPages" @click="customerPage++" class="btn-page">{{ $t('wholesaler.CreatePackage.nextPage') }}</button>
          </div>
          <div v-if="filteredCustomers.length === 0 && !customerSearchQuery" class="empty-results">
            <span class="empty-icon">🏪</span>
            <p>{{ $t('wholesaler.CreatePackage.noStallData') }}</p>
          </div>
          <div v-if="filteredCustomers.length === 0 && customerSearchQuery" class="empty-results">
            <span class="empty-icon">🔍</span>
            <p>{{ $t('wholesaler.CreatePackage.noMatchingStall') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div class="bottom-actions">
      <button class="btn-cancel" @click="goBack">{{ $t('wholesaler.CreatePackage.cancel') }}</button>
      <button class="btn-submit" @click="savePackage" :disabled="submitting">
        {{ submitting ? $t('wholesaler.CreatePackage.saving') : $t('wholesaler.CreatePackage.save') }}
      </button>
    </div>
  </div>
</template>

<script>
import { createPackage } from '@/api/wholesaler/package'
import { getProductList } from '@/api/wholesaler/product'
import api from '@/services/api'
import { getWholesalerId } from '@/utils/wholesaler'

export default {
  name: 'CreateWholesalePackage',
  data() {
    return {
      isEditMode: false,
      packageId: null,
      form: {
        packageName: '',
        description: '',
        validStartDate: '',
        validEndDate: ''
      },
      availableProducts: [],
      availableCustomers: [],
      historyPackages: [], // 历史批发报价（按包分组）
      selectedProductIds: [],
      selectedCustomerIds: [],
      productPrices: {},
      originalPrices: {},  // 保存原始价格（编辑模式用批发价，创建模式用市场价）
      selectAllProducts: false,
      submitting: false,
      // 批量调价
      batchAdjustValue: null,
      // UI 状态
      customerSearchQuery: '',
      // 分页
      customerPage: 1,
      customerPageSize: 20,
      totalCustomers: 0
    }
  },
  async mounted() {
    // 先加载商品和档口
    await this.loadProducts()
    await this.loadCustomers()
    
    // 检查是否是编辑模式（在商品加载后）
    const packageId = this.$route.query.packageId
    if (packageId) {
      this.isEditMode = true
      this.packageId = parseInt(packageId)
      console.log('📝 编辑模式，批发报价 ID:', this.packageId)
      await this.loadPackageDetail()
    }
  },
  computed: {
    // 搜索过滤后的档口列表
    filteredCustomers() {
      if (!this.customerSearchQuery) {
        return this.availableCustomers
      }
      const query = this.customerSearchQuery.toLowerCase()
      return this.availableCustomers.filter(c => 
        c.name.toLowerCase().includes(query) ||
        (c.contactPerson && c.contactPerson.toLowerCase().includes(query)) ||
        (c.contactPhone && c.contactPhone.includes(query))
      )
    },
    
    // 总页数
    customerTotalPages() {
      return Math.ceil(this.filteredCustomers.length / this.customerPageSize) || 1
    },
    
    // 当前页数据
    paginatedCustomers() {
      const start = (this.customerPage - 1) * this.customerPageSize
      const end = start + this.customerPageSize
      return this.filteredCustomers.slice(start, end)
    }
  },
  
  methods: {
    async loadProducts() {
      try {
        // 获取批发商 ID
        const wholesalerId = getWholesalerId() ?? 1
        
        const res = await getProductList(wholesalerId)
        console.log('📡 商品 API 返回:', res)
        // 拦截器已解包 response.data.data，res 可能是：
        // 1. 直接数组 [...]
        // 2. 分页对象 { content: [...], totalItems: N }
        // 3. 嵌套对象 { data: [...] }
        if (Array.isArray(res)) {
          this.availableProducts = res
        } else if (res && Array.isArray(res.content)) {
          this.availableProducts = res.content
        } else if (res && Array.isArray(res.data)) {
          this.availableProducts = res.data
        } else {
          this.availableProducts = []
        }
        
        console.log('✅ 加载商品列表:', this.availableProducts.length, '个商品')
        
        // ⭐ 初始化所有商品的 originalPrices 为市场价（创建模式用）
        this.availableProducts.forEach(p => {
          this.originalPrices[p.id] = p.marketPrice || p.price || 0
        })
        
        // 检查是否有 URL 参数传入的已选商品 ID（从商品批发最低价进来）
        const preSelectedIds = this.$route.query.productIds
        if (preSelectedIds) {
          const ids = preSelectedIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
          if (ids.length > 0) {
            this.selectedProductIds = ids
            this.selectAllProducts = ids.length === this.availableProducts.length
            
            // ⭐ 自动填充批发价：优先用最低批发价，没有则用市场价
            ids.forEach(id => {
              const product = this.availableProducts.find(p => p.id === id)
              if (product && !this.productPrices[id]) {
                // 优先用最低批发价，没有则用市场价
                const defaultPrice = product.minWholesalePrice || product.marketPrice || product.price || 0
                this.productPrices[id] = defaultPrice
                this.originalPrices[id] = defaultPrice
                console.log('✅ 商品', id, '默认批发价:', defaultPrice, '(来源:', product.minWholesalePrice ? '最低批发价' : '市场价', ')')
              }
            })
            
            console.log('✅ 自动选中商品:', ids)
          }
        }
      } catch (error) {
        console.error('加载商品列表失败:', error)
        this.$message.error('加载商品列表失败')
      }
    },
    async loadCustomers() {
      try {
        console.log('🔄 开始加载档口数据...')
        
        // 调用 API 获取当前批发商的全部档口（一次加载所有，前端分页）
        const res = await api.get('/wholesaler/stalls', { params: { page: 1, size: 9999 } })
        console.log('📡 档口 API 返回:', res)
        
        // 后端返回 { items: [...], total, page, size, totalPages }
        const content = Array.isArray(res) ? res : (res?.items || res?.data?.items || [])
        
        if (Array.isArray(content) && content.length > 0) {
          this.availableCustomers = content.map(customer => {
            // ⭐ 使用 stallId（实际档口 id）而不是 id（mapping 表 id）
            let customerId = customer.stallId || customer.id
            
            // 如果 id 是对象，尝试提取实际值
            if (typeof customerId === 'object' && customerId !== null) {
              customerId = customerId.id || customerId.value || Object.values(customerId)[0]
            }
            
            return {
              id: Number(customerId),
              name: customer.customerName || customer.shopName || '未知档口',
              contactPerson: customer.contactPerson || '',
              contactPhone: customer.phone || '',
              area: customer.regionName || '',
              marketType: customer.customerType || ''
            }
          })
          this.totalCustomers = this.availableCustomers.length
          console.log('✅ 加载档口列表:', this.availableCustomers.length, '个档口')
        } else {
          console.warn('⚠️ 档口 API 返回空数据')
          this.availableCustomers = []
        }
        
        // 加载历史选择的档口（从批发客户关系表）
        await this.loadHistoricalCustomers()
      } catch (error) {
        console.error('❌ 加载档口列表失败:', error)
        this.availableCustomers = []
      }
    },
    
    // 加载历史选择的档口（从批发报价→批发客户关系表，按包分组）
    async loadHistoricalCustomers() {
      try {
        console.log('🔄 开始加载历史批发报价...')
        
        // 1. 获取所有批发报价列表
        const { getPackageList } = await import('@/api/wholesaler/package')
        const packageRes = await getPackageList()
        console.log('📡 批发报价 API 返回:', packageRes)
        
        const packages = Array.isArray(packageRes) ? packageRes : (packageRes.data || [])
        
        // 2. 按批发报价分组
        this.historyPackages = packages
          .filter(pkg => pkg.customers && pkg.customers.length > 0)
          .map(pkg => ({
            id: pkg.id,
            packageName: pkg.packageName || '未知批发报价',
            createdDate: this.formatDate(pkg.createdAt),
            customers: pkg.customers.map(c => ({
              id: c.customerId || c.id,
              name: c.name || '未知档口',
              contactPerson: c.contactPerson || '',
              contactPhone: c.contactPhone || ''
            }))
          }))
          .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)) // 按日期倒序
        
        console.log('✅ 加载历史批发报价:', this.historyPackages.length, '个')
        if (this.historyPackages.length > 0) {
          console.log('📊 第一个批发报价:', this.historyPackages[0])
        }
      } catch (error) {
        console.error('❌ 加载历史档口失败:', error)
        this.historyPackages = []
      }
    },
    
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${month}-${day}`
    },
    
    // ⭐ 加载批发报价详情（编辑模式）
    async loadPackageDetail() {
      try {
        const { getPackageDetail } = await import('@/api/wholesaler/package')
        const res = await getPackageDetail(this.packageId)
        const pkg = res.data || res
        
        console.log('📝 加载批发报价详情:', pkg)
        
        // 填充基本信息
        this.form.packageName = pkg.packageName
        this.form.description = pkg.description || ''
        this.form.validStartDate = pkg.validStartDate ? pkg.validStartDate.split('T')[0] : ''
        this.form.validEndDate = pkg.validEndDate ? pkg.validEndDate.split('T')[0] : ''
        
        // 填充商品和价格
        if (pkg.products && pkg.products.length > 0) {
          this.selectedProductIds = pkg.products.map(p => p.productId)
          pkg.products.forEach(p => {
            const price = p.wholesalePrice || p.price
            this.productPrices[p.productId] = price
            // ⭐ 只更新有批发价的商品，其他商品保持市场价
            this.originalPrices[p.productId] = price
          })
        }
        
        // 填充客户
        if (pkg.customers && pkg.customers.length > 0) {
          this.selectedCustomerIds = pkg.customers.map(c => {
            // ⭐ customerId 就是档口表的真实 ID（后端 wholesale_package_customer 表存的就是 customer.id）
            let customerId = c.customerId || c.id
            if (typeof customerId === 'object' && customerId !== null) {
              customerId = customerId.id || customerId.value || Object.values(customerId)[0]
            }
            return Number(customerId)
          })
        }
        
        console.log('✅ 批发报价详情加载完成')
        console.log('📊 originalPrices:', this.originalPrices)
      } catch (error) {
        console.error('❌ 加载批发报价详情失败:', error)
        this.$message.error('加载批发报价详情失败')
      }
    },
    getProductIcon(product) {
      const icons = { '水果类': '🍎', '蔬菜类': '🥬', '肉类': '🥩', '水产': '🐟', '粮油': '🌾' }
      return icons[product.categoryName] || '📦'
    },
    /**
     * 计算折扣文本（如 8.5折）
     */
    getDiscountText(wholesalePrice, marketPrice) {
      if (!wholesalePrice || !marketPrice || marketPrice <= 0) return '0';
      const discount = (wholesalePrice / marketPrice) * 10;
      return discount % 1 === 0 ? `${discount}` : `${discount.toFixed(1)}`;
    },
    handleSelectAllProducts() {
      if (this.selectAllProducts) {
        // 全选
        this.selectedProductIds = this.availableProducts.map(p => p.id)
        // 为所有商品初始化批发价
        this.availableProducts.forEach(p => {
          if (!this.productPrices[p.id]) {
            // 编辑模式用原价，创建模式用市场价
            const defaultPrice = this.originalPrices[p.id] || p.marketPrice
            this.productPrices[p.id] = Math.round(defaultPrice * 100) / 100
          }
        })
      } else {
        // 全不选，清空价格
        this.selectedProductIds = []
        this.productPrices = {}
      }
    },
    
    // 单个商品勾选状态变化
    handleProductCheck(productId, checked) {
      if (checked) {
        // 勾选时初始化批发价
        const product = this.availableProducts.find(p => p.id === productId)
        if (product && !this.productPrices[productId]) {
          // 优先用 originalPrices（编辑模式为批发价，创建模式为市场价）
          const defaultPrice = this.originalPrices[productId] || product.marketPrice
          this.productPrices[productId] = Math.round(defaultPrice * 100) / 100
          console.log('✅ 勾选商品，设置默认批发价:', productId, defaultPrice)
        }
      } else {
        // 取消勾选时删除价格（恢复默认）
        delete this.productPrices[productId]
        console.log('❌ 取消勾选商品，删除批发价:', productId)
      }
    },
    handleSelectAllCustomers() {
      if (this.selectAllCustomers) {
        this.selectedCustomerIds = this.availableCustomers.map(c => c.id)
      } else {
        this.selectedCustomerIds = []
      }
    },
    
    // UI 方法
    removeCustomer(id) {
      this.selectedCustomerIds = this.selectedCustomerIds.filter(cid => cid !== id)
    },
    
    getCustomerName(id) {
      if (!id) return '未知档口'
      // 在已加载的档口列表中查找
      const customer = this.availableCustomers?.find(c => c.id === id)
      if (customer && customer.name) {
        return customer.name
      }
      // 如果找不到，返回一个默认名称
      return '档口 ' + id
    },
    
    addCustomer(id) {
      // 如果已存在，不重复添加
      if (this.selectedCustomerIds.includes(id)) {
        return
      }
      this.selectedCustomerIds.push(id)
    },
    
    // 添加整个批发报价的所有档口
    addPackageCustomers(pkg) {
      pkg.customers.forEach(customer => {
        if (!this.selectedCustomerIds.includes(customer.id)) {
          this.selectedCustomerIds.push(customer.id)
        }
      })
    },
    
    // 检查批发报价的档口是否已全部添加
    hasPackageCustomers(pkg) {
      return pkg.customers.every(c => this.selectedCustomerIds.includes(c.id))
    },
    
    // 获取已选的档口数量
    getSelectedPackageCount(pkg) {
      return pkg.customers.filter(c => this.selectedCustomerIds.includes(c.id)).length
    },
    
    clearSearch() {
      this.customerSearchQuery = ''
      this.customerPage = 1
    },
    
    // 当页全选
    selectAllOnPage() {
      const pageIds = this.paginatedCustomers.map(c => c.id)
      pageIds.forEach(id => {
        if (!this.selectedCustomerIds.includes(id)) {
          this.selectedCustomerIds.push(id)
        }
      })
      this.$message.success(`已选中当页 ${pageIds.length} 个档口`)
    },
    
    // 所有档口全选
    doSelectAllCustomers() {
      const allIds = this.filteredCustomers.map(c => c.id)
      this.selectedCustomerIds = allIds
      this.$message.success(`已选中全部 ${allIds.length} 个档口`)
    },
    
    // 清除所有已选档口
    clearAllCustomers() {
      this.selectedCustomerIds = []
      this.$message.success('已清除所有档口')
    },
    
    // 应用批量调减
    applyBatchAdjust() {
      if (!this.batchAdjustValue || this.batchAdjustValue <= 0) {
        this.$message.warning('请输入有效的调减金额')
        return
      }
      
      let count = 0
      this.selectedProductIds.forEach(id => {
        const product = this.availableProducts.find(p => p.id === id)
        if (product) {
          const marketPrice = product.marketPrice || product.price || 0
          // 批发价 = 市场价 - 调减金额
          const newPrice = marketPrice - this.batchAdjustValue
          // 四舍五入到分（2 位小数），使用 $set 确保响应式更新
          const roundedPrice = Math.max(0, Math.round(newPrice * 100) / 100)
          this.$set(this.productPrices, id, roundedPrice)
          count++
        }
      })
      
      this.$message.success(`已为 ${count} 个商品调减 ¥${this.batchAdjustValue}`)
      this.batchAdjustValue = null
    },
    
    // 应用折扣
    applyDiscount(discount) {
      const selectedCount = this.selectedProductIds.length
      console.log('🔍 应用折扣:', discount, '折，已选商品数:', selectedCount)
      console.log('🔍 已选商品 ID:', this.selectedProductIds)
      
      if (selectedCount === 0) {
        this.$message.warning('请先勾选商品')
        return
      }
      
      let count = 0
      this.selectedProductIds.forEach(id => {
        const product = this.availableProducts.find(p => p.id === id)
        console.log('🔍 查找商品 ID:', id, '结果:', product)
        
        if (product) {
          const marketPrice = product.marketPrice || product.price || 0
          console.log('🔍 商品:', product.name, '市场价:', marketPrice)
          
          // 批发价 = 市场价 × 折扣率
          const newPrice = marketPrice * (discount / 10)
          // 四舍五入到 4 位小数，使用 $set 确保响应式更新
          const roundedPrice = Math.round(newPrice * 10000) / 10000
          this.$set(this.productPrices, id, roundedPrice)
          
          console.log('✅ 设置批发价:', this.productPrices[id])
          count++
        }
      })
      
      console.log('✅ 已更新', count, '个商品的批发价')
      this.$forceUpdate() // 强制更新视图
      this.$message.success(`已为 ${count} 个商品应用 ${discount}折`)
    },
    async savePackage() {
      // 验证
      if (!this.form.packageName) {
        this.$message.warning('请输入批发报价名称')
        return
      }
      if (this.selectedProductIds.length === 0) {
        this.$message.warning('请至少选择一个商品')
        return
      }
      if (this.selectedCustomerIds.length === 0) {
        this.$message.warning('请至少选择一个档口')
        return
      }

      // 验证商品批发价
      for (const productId of this.selectedProductIds) {
        if (!this.productPrices[productId]) {
          this.$message.warning('请为所有选中商品设置批发价')
          return
        }
      }

      try {
        this.submitting = true
        
        // 构建商品列表
        const products = this.selectedProductIds.map(id => {
          const product = this.availableProducts.find(p => p.id === id)
          return {
            productId: id,
            wholesalePrice: parseFloat(this.productPrices[id]),
            name: product?.name,
            marketPrice: product?.marketPrice
          }
        })

        const packageData = {
          packageName: this.form.packageName,
          description: this.form.description,
          validStartDate: this.form.validStartDate ? new Date(this.form.validStartDate).toISOString() : null,
          validEndDate: this.form.validEndDate ? new Date(this.form.validEndDate).toISOString() : null,
          products,
          customerIds: this.selectedCustomerIds  // 后端要求传 customerIds 数组
        }
        
        // ⭐ 根据模式调用不同 API
        if (this.isEditMode) {
          // 编辑模式
          const { updatePackage } = await import('@/api/wholesaler/package')
          await updatePackage(this.packageId, packageData)
          this.$message.success('批发报价更新成功')
        } else {
          // 创建模式
          await createPackage(packageData)
          this.$message.success('创建成功')
        }

        this.$router.back()
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error(error.message || '保存失败')
      } finally {
        this.submitting = false
      }
    },
    goBack() {
      this.$router.back()
    }
  },
  
  watch: {
    customerSearchQuery() {
      this.customerPage = 1
    }
  }
}
</script>

<style scoped>
.create-package-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 80px;
}

.back-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  font-size: 20px;
  cursor: pointer;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.2s;
}

.back-btn:active {
  background: rgba(255, 255, 255, 0.2);
}

.back-header h1 {
  font-size: 17px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.header-icon {
  font-size: 20px;
  cursor: pointer;
}

.form-container {
  padding: 12px;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.btn-clear-all {
  padding: 6px 12px;
  background: #fff1f0;
  border: 1px solid #ffa39e;
  border-radius: 4px;
  font-size: 12px;
  color: #ff4d4f;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-clear-all:hover {
  background: #ff4d4f;
  color: white;
  border-color: #ff4d4f;
}

/* 批量调减栏 */
.batch-adjust-bar {
  background: linear-gradient(135deg, #f6ffed, #d9f7be);
  border: 2px solid #52c41a;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
}

.batch-label {
  color: #52c41a;
  font-weight: 600;
  white-space: nowrap;
  font-size: 14px;
}

.batch-input {
  flex: 1;
  padding: 8px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  text-align: center;
  font-size: 14px;
}

.batch-input:focus {
  outline: none;
  border-color: #52c41a;
}

.batch-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.batch-btn:hover {
  opacity: 0.9;
}

/* 快捷折扣标签 */
.discount-tags {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.discount-tag {
  padding: 6px 12px;
  background: white;
  border: 1px solid #52c41a;
  color: #52c41a;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.discount-tag:hover {
  background: #52c41a;
  color: white;
}

/* 节省标签 */
.save-tag {
  background: linear-gradient(135deg, #ff6b6b, #ff4d4f);
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 11px;
  margin-left: 6px;
  font-weight: 600;
}

/* 折扣徽章 */
.discount-badge {
  background: #52c41a;
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 11px;
  margin-left: 6px;
  font-weight: 600;
}

.required {
  color: #ff4d4f;
}

.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
}

.form-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-range input {
  flex: 1;
}

/* 第 1 层：已选档口列表 */
.selected-layer {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 8px;
  min-height: 40px;
}

.selected-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
}

.selected-item:hover {
  border-color: #ff4d4f;
  color: #ff4d4f;
}

.selected-item .remove-hint {
  color: #999;
  font-size: 14px;
  font-weight: 600;
}

.selected-item:hover .remove-hint {
  color: #ff4d4f;
}

/* 第 2 层：搜索框 */
.search-layer {
  margin-bottom: 12px;
}

.search-input-full {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
}

.search-input-full:focus {
  outline: none;
  border-color: #667eea;
}

/* 第 3 层：历史选择（紧凑卡片） */
.history-layer {
  margin-bottom: 12px;
}

.history-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  padding-left: 8px;
  border-left: 3px solid #667eea;
}

.history-packages {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 8px;
}

.history-package-card {
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 10px 12px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.history-package-card:hover {
  border-color: #667eea;
  background: #f5f5ff;
}

.history-package-card.is-selected {
  border-color: #52c41a;
  background: #f6ffed;
}

.package-card-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.package-card-name {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.package-card-meta {
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 11px;
}

.package-card-date {
  color: #999;
}

.package-card-count {
  color: #667eea;
  font-weight: 500;
  background: #f0f5ff;
  padding: 1px 6px;
  border-radius: 8px;
}

.package-selected-mark {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  font-size: 11px;
  color: #52c41a;
  font-weight: 600;
  text-align: center;
}

.history-more {
  text-align: center;
  padding: 8px;
  font-size: 11px;
  color: #999;
  background: #fafafa;
  border-radius: 6px;
  margin-top: 8px;
}

.selected-mark {
  font-size: 12px;
  color: #52c41a;
  font-weight: 600;
}

/* 第 4 层：搜索结果 */
.results-layer {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  background: white;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
  flex-wrap: wrap;
  gap: 8px;
}

.results-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.results-count {
  font-size: 12px;
  color: #999;
}

.select-all-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-select-all {
  padding: 6px 12px;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-select-all:hover {
  border-color: #667eea;
  color: #667eea;
}

.btn-select-all-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
}

.btn-select-all-primary:hover {
  opacity: 0.9;
}

.btn-clear-search {
  padding: 4px 12px;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

.btn-clear-search:hover {
  border-color: #667eea;
  color: #667eea;
}

.results-list {
  max-height: 300px;
  overflow-y: auto;
}

.result-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s;
}

.result-item:last-child {
  border-bottom: none;
}

.result-item:hover {
  background: #f5f5ff;
}

.result-item.is-selected {
  background: #f6ffed;
}

.result-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.result-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.result-meta {
  font-size: 12px;
  color: #999;
}

.empty-results {
  text-align: center;
  padding: 40px 20px;
}

.empty-results .empty-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 12px;
}

.empty-results p {
  font-size: 14px;
  color: #999;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.btn-page {
  padding: 8px 16px;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-page:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
}

.btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: #666;
}

/* 选择器面板 */
.selector-panel {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  background: white;
}

/* Tab 切换 */
.selector-tabs {
  display: flex;
  border-bottom: 1px solid #e8e8e8;
}

.tab {
  flex: 1;
  padding: 12px;
  background: white;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s;
}

.tab.active {
  color: #667eea;
  border-bottom-color: #667eea;
  font-weight: 600;
}

/* 历史选择面板 */
.history-panel {
  padding: 12px;
}

.history-info {
  padding: 10px 12px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 6px;
  font-size: 13px;
  color: #0050b3;
  margin-bottom: 12px;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.history-item:hover {
  border-color: #667eea;
  background: #f5f5ff;
}

.history-item.is-selected {
  border-color: #52c41a;
  background: #f6ffed;
}

.selected-mark {
  color: #52c41a;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  background: #f6ffed;
  border-radius: 12px;
}

.history-icon {
  font-size: 24px;
}

.history-info-text {
  flex: 1;
}

.history-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.history-contact {
  font-size: 12px;
  color: #999;
}

.history-count {
  font-size: 12px;
  color: #667eea;
  font-weight: 500;
  padding: 4px 8px;
  background: #f0f5ff;
  border-radius: 12px;
}

.empty-history {
  text-align: center;
  padding: 40px 20px;
}

.empty-history .empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.empty-history .empty-text {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.empty-history .empty-desc {
  font-size: 12px;
  color: #999;
}

/* 全部档口面板 */
.all-customers-panel {
  padding: 12px;
}

.search-bar {
  margin-bottom: 12px;
}

.search-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 13px;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
}

.batch-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f9f9f9;
  border-radius: 6px;
}

.select-all {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.btn-reverse,
.btn-clear {
  margin-left: auto;
  padding: 6px 12px;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

.btn-reverse:hover,
.btn-clear:hover {
  border-color: #667eea;
  color: #667eea;
}

/* 商品选择器 */
.product-selector, .customer-selector {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.selector-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;
  font-weight: 600;
}

.select-all {
  width: 18px;
  height: 18px;
}

.product-list, .customer-list {
  max-height: 300px;
  overflow-y: auto;
}

.product-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s;
}

.product-item:last-child {
  border-bottom: none;
}

.product-item.selected {
  background: #f6ffed;
}

.product-checkbox, .customer-checkbox {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.product-image {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #f0f0f0, #e0e0e0);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.product-info {
  flex: 1;
  min-width: 0;
}

.product-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.product-meta {
  font-size: 12px;
  color: #999;
}

.product-price-input {
  flex-shrink: 0;
}

.product-price-input label {
  display: block;
  font-size: 11px;
  color: #999;
  text-align: center;
  margin-bottom: 4px;
}

.price-input {
  width: 80px;
  padding: 6px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
}

.price-input:focus {
  outline: none;
  border-color: #667eea;
}

.customer-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s;
}

.customer-item:last-child {
  border-bottom: none;
}

.customer-item.selected {
  background: #e6f4ff;
}

.customer-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.customer-info {
  flex: 1;
}

.customer-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.customer-contact {
  font-size: 12px;
  color: #999;
}

/* 底部操作栏 */
.bottom-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
}

.btn-cancel, .btn-submit {
  flex: 1;
  padding: 14px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.btn-cancel {
  background: #f0f0f0;
  color: #666;
}

.btn-submit {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
